// src/componentes/Encabezado.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell, ChevronDown, LayoutDashboard, CalendarClock, Users, FilePlus2,
  ListTodo, CheckCircle2, History, BarChart3, Receipt, FileStack, BellRing,
  UserRound, Boxes, LogOut, Menu as MenuIcon,
} from 'lucide-react';
import { obtenerNotificacionesPendientes, obtenerTecnicosOperativa } from '../servicios/api';
import {
  activarNotificacionesPush,
  desactivarNotificacionesPush,
  tieneSuscripcionPush,
} from '../servicios/notificacionesPush';

// Componente para el encabezado, principalmente para la vista móvil.
const Encabezado = ({ toggleSidebar, onOpenModal, isMobileSidebar }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    localStorage.removeItem('filtro_tecnico_id');
    navigate('/iniciar-sesion');
  };
  const [avisosAbierto, setAvisosAbierto] = useState(false);
  const [resumenFinancieroAbierto, setResumenFinancieroAbierto] = useState(false);
  const [clientesAbierto, setClientesAbierto] = useState(false);
  const [stockAbierto, setStockAbierto] = useState(false);
  const [notificacionesAbiertas, setNotificacionesAbiertas] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [cantidadNotificaciones, setCantidadNotificaciones] = useState(0);
  const [pushActivo, setPushActivo] = useState(false);
  const [pushCargando, setPushCargando] = useState(false);
  const [usuario] = useState(() => {
    try { return JSON.parse(localStorage.getItem('usuario') || 'null'); } catch { return null; }
  });
  const [tecnicosFiltro, setTecnicosFiltro] = useState([]);
  const [tecnicoGlobalId, setTecnicoGlobalId] = useState(() => localStorage.getItem('filtro_tecnico_id') || '');
  const esAdministrador = usuario?.rol === 'administrador';

  useEffect(() => {
    if (usuario?.rol !== 'tecnico') return undefined;
    let activo = true;
    tieneSuscripcionPush()
      .then((suscrito) => {
        if (activo) setPushActivo(suscrito);
      })
      .catch((error) => console.error('No se pudo comprobar la suscripción push:', error));
    return () => {
      activo = false;
    };
  }, [usuario?.rol]);

  const cambiarNotificacionesPush = async () => {
    setPushCargando(true);
    try {
      if (pushActivo) {
        await desactivarNotificacionesPush();
        setPushActivo(false);
        window.alert('Notificaciones desactivadas en este dispositivo.');
      } else {
        await activarNotificacionesPush();
        setPushActivo(true);
        window.alert('Notificaciones activadas. Recibirás avisos de nuevos servicios en este dispositivo.');
      }
    } catch (pushError) {
      window.alert(pushError.response?.data?.mensaje || pushError.message || 'No se pudo cambiar la configuración de notificaciones.');
    } finally {
      setPushCargando(false);
    }
  };

  useEffect(() => {
    if (!esAdministrador) return;
    obtenerTecnicosOperativa()
      .then((respuesta) => setTecnicosFiltro(respuesta.tecnicos || []))
      .catch((errorTecnicos) => console.error('Error al cargar técnicos para el filtro global:', errorTecnicos));
  }, [esAdministrador]);

  const cambiarFiltroTecnico = (event) => {
    const tecnicoId = event.target.value;
    setTecnicoGlobalId(tecnicoId);
    if (tecnicoId) localStorage.setItem('filtro_tecnico_id', tecnicoId);
    else localStorage.removeItem('filtro_tecnico_id');
    window.dispatchEvent(new CustomEvent('filtro-tecnico-cambiado', { detail: { tecnicoId } }));
  };

  useEffect(() => {
    let mounted = true;

    const cargarNotificaciones = async () => {
      try {
        const respuesta = await obtenerNotificacionesPendientes();
        if (mounted && respuesta.exito) {
          setNotificaciones(respuesta.notificaciones || []);
          setCantidadNotificaciones(Number(respuesta.cantidad || 0));
        }
      } catch (error) {
        if (mounted) {
          setNotificaciones([]);
          setCantidadNotificaciones(0);
        }
      }
    };

    cargarNotificaciones();
    const intervalId = window.setInterval(cargarNotificaciones, 60000);
    window.addEventListener('notificaciones-actualizadas', cargarNotificaciones);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener('notificaciones-actualizadas', cargarNotificaciones);
    };
  }, []);

  const handleMenuClick = (menuName) => {
    setAvisosAbierto(menuName === 'avisos' ? !avisosAbierto : false);
    setResumenFinancieroAbierto(menuName === 'finanzas' ? !resumenFinancieroAbierto : false);
    setClientesAbierto(menuName === 'clientes' ? !clientesAbierto : false);
    setStockAbierto(menuName === 'stock' ? !stockAbierto : false);
  };

  const handleSubMenuClick = () => {
    setAvisosAbierto(false);
    setResumenFinancieroAbierto(false);
    setClientesAbierto(false);
    setStockAbierto(false);
    if (isMobileSidebar) {
      toggleSidebar();
    }
  };

  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setAvisosAbierto(false);
        setResumenFinancieroAbierto(false);
        setClientesAbierto(false);
        setStockAbierto(false);
        setNotificacionesAbiertas(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clases reutilizables para mantener coherencia visual entre los botones del menú y sus submenús.
  const claseBotonNivelUno = (abierto) => `group flex w-full items-center justify-between gap-2 rounded-lg py-2.5 px-3 text-sm font-semibold tracking-normal transition-colors duration-150 md:w-auto md:py-2 ${abierto ? 'bg-slate-800 text-brand-orange' : 'text-slate-200 hover:bg-slate-800/80 hover:text-brand-orange'} focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/60`;
  const clasePanelSubmenu = 'mt-1 space-y-0.5 rounded-lg border border-transparent bg-slate-800/40 p-1.5 md:absolute md:top-full md:left-0 md:mt-2 md:min-w-[15rem] md:origin-top-left md:animate-menu-in md:rounded-xl md:border-slate-700/80 md:bg-slate-900/95 md:p-2 md:shadow-2xl md:shadow-black/40 md:backdrop-blur-sm';
  const claseItemSubmenu = 'flex items-center gap-2.5 rounded-lg py-2 px-2.5 text-[0.95rem] font-medium text-slate-300 transition-colors duration-150 hover:bg-slate-800 hover:text-brand-orange md:text-sm whitespace-nowrap';

  return (
    <header ref={menuRef} className={`bg-gray-900 text-white p-4 flex ${isMobileSidebar ? 'flex-col items-start' : 'justify-between items-center md:flex-row md:items-center md:justify-between md:border-b md:border-slate-800/80 md:py-0 md:px-0 md:space-x-8'} z-50`}>
      {!isMobileSidebar && (
        <Link to="/" className="text-white text-xl font-semibold uppercase md:hidden">
          <span className="text-2xl font-extrabold tracking-tight text-brand-orange">Saasfix</span>
        </Link>
      )}

      {!isMobileSidebar && (
        <button onClick={toggleSidebar} className="p-2 text-slate-200 hover:text-brand-orange md:hidden">
          <MenuIcon className="h-6 w-6" />
        </button>
      )}

      <nav className={`${isMobileSidebar ? 'flex flex-col w-full space-y-6' : 'hidden md:flex'} flex-1 md:flex-none md:flex md:items-center md:space-x-1.5`}>
        {isMobileSidebar && (
          <span className="mb-2 px-3 text-3xl font-extrabold tracking-tight text-brand-orange">Saasfix</span>
        )}
        <div className="relative group">
          <button
            onClick={() => handleMenuClick('avisos')}
            className={claseBotonNivelUno(avisosAbierto)}
          >
            <span className="flex items-center text-lg md:text-sm leading-none">
              <CalendarClock className="mr-2 h-5 w-5 md:h-4 md:w-4" />
              Avisos
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 transform transition-transform duration-200 ${avisosAbierto ? 'rotate-180 md:rotate-0' : ''} md:ml-1 md:h-3.5 md:w-3.5 md:opacity-60`} />
          </button>
          {avisosAbierto && (
            <div className={clasePanelSubmenu}>
              <Link to="/dashboard" className={claseItemSubmenu} onClick={handleSubMenuClick}><LayoutDashboard className="h-4 w-4 shrink-0 text-slate-500" />Panel de Control</Link>
              <Link to="/avisos-del-dia" className={claseItemSubmenu} onClick={handleSubMenuClick}><CalendarClock className="h-4 w-4 shrink-0 text-slate-500" />Avisos del Día</Link>
              {esAdministrador && <Link to="/gestion-tecnicos" className={claseItemSubmenu} onClick={handleSubMenuClick}><Users className="h-4 w-4 shrink-0 text-slate-500" />Gestión de Técnicos</Link>}
              <button onClick={() => { onOpenModal(); handleSubMenuClick(); }} className={`${claseItemSubmenu} w-full text-left`}><FilePlus2 className="h-4 w-4 shrink-0 text-slate-500" />Crear Avisos</button>
              <Link to="/avisos-pendientes" className={claseItemSubmenu} onClick={handleSubMenuClick}><ListTodo className="h-4 w-4 shrink-0 text-slate-500" />Avisos Pendientes</Link>
              <Link to="/avisos-completados" className={claseItemSubmenu} onClick={handleSubMenuClick}><CheckCircle2 className="h-4 w-4 shrink-0 text-slate-500" />Avisos Completados</Link>
              <Link to="/historial-servicios" className={claseItemSubmenu} onClick={handleSubMenuClick}><History className="h-4 w-4 shrink-0 text-slate-500" />Historial de Servicios</Link>
            </div>
          )}
        </div>

        <div className="relative group">
          <button onClick={() => handleMenuClick('finanzas')} className={claseBotonNivelUno(resumenFinancieroAbierto)}>
            <span className="flex items-center text-lg md:text-sm leading-none">
              <BarChart3 className="mr-2 h-5 w-5 md:h-4 md:w-4" />
              {esAdministrador ? 'Finanzas' : 'Facturación'}
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 transform transition-transform duration-200 ${resumenFinancieroAbierto ? 'rotate-180 md:rotate-0' : ''} md:ml-1 md:h-3.5 md:w-3.5 md:opacity-60`} />
          </button>
          {resumenFinancieroAbierto && (
            <div className={clasePanelSubmenu}>
              {esAdministrador && <Link to="/finanzas" className={claseItemSubmenu} onClick={handleSubMenuClick}><BarChart3 className="h-4 w-4 shrink-0 text-slate-500" />Finanzas</Link>}
              <Link to="/facturacion" className={claseItemSubmenu} onClick={handleSubMenuClick}><Receipt className="h-4 w-4 shrink-0 text-slate-500" />Facturación</Link>
              {esAdministrador && <Link to="/albaranes-presupuestos" className={claseItemSubmenu} onClick={handleSubMenuClick}><FileStack className="h-4 w-4 shrink-0 text-slate-500" />Albaranes y Presupuestos</Link>}
            </div>
          )}
        </div>

        <div className="relative group">
          <Link to="/clientes" className={claseBotonNivelUno(false)} onClick={handleSubMenuClick}>
            <span className="flex items-center text-lg md:text-sm leading-none">
              <UserRound className="mr-2 h-5 w-5 md:h-4 md:w-4" />
              Clientes
            </span>
          </Link>
        </div>

        <div className="relative group">
          <Link to="/inventario" className={claseBotonNivelUno(false)} onClick={handleSubMenuClick}>
            <span className="flex items-center text-lg md:text-sm leading-none">
              <Boxes className="mr-2 h-5 w-5 md:h-4 md:w-4" />
              Stock
            </span>
          </Link>
        </div>
      </nav>
      {!isMobileSidebar && (
        <div className="ml-auto flex items-center gap-2">
          {esAdministrador && (
            <label className="hidden items-center gap-2 text-xs font-medium text-slate-400 lg:flex">
              Vista
              <select value={tecnicoGlobalId} onChange={cambiarFiltroTecnico} aria-label="Filtrar toda la aplicación por técnico" className="max-w-48 rounded-lg border border-slate-700 bg-slate-800 px-2 py-2 text-sm text-slate-100 transition-colors focus:border-brand-orange focus:outline-none">
                <option value="">Vista general</option>
                {tecnicosFiltro.map((tecnico) => <option key={tecnico.id} value={tecnico.id}>{tecnico.nombre}</option>)}
              </select>
            </label>
          )}
          {usuario?.rol === 'tecnico' && (
            <button
              type="button"
              onClick={cambiarNotificacionesPush}
              disabled={pushCargando}
              aria-label={pushActivo ? 'Desactivar notificaciones de avisos' : 'Activar notificaciones de avisos'}
              title={pushActivo ? 'Notificaciones push activas' : 'Activar avisos de nuevos servicios'}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-700 px-3 text-sm text-slate-200 transition-colors hover:bg-slate-800 disabled:opacity-60"
            >
              <BellRing size={18} className={pushActivo ? 'text-emerald-400' : 'text-orange-300'} />
              <span className="hidden xl:inline">
                {pushCargando ? 'Guardando…' : pushActivo ? 'Avisos activos' : 'Activar avisos'}
              </span>
            </button>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={() => setNotificacionesAbiertas((abiertas) => !abiertas)}
              aria-label={`Notificaciones de facturación: ${cantidadNotificaciones} pendientes`}
              aria-expanded={notificacionesAbiertas}
              className="relative grid h-10 w-10 place-items-center rounded-lg text-slate-300 transition-colors hover:bg-slate-800 hover:text-brand-orange focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/60"
              title="Notificaciones pendientes de facturación"
            >
              <Bell size={20} />
              {cantidadNotificaciones > 0 && (
                <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-600 px-1 text-center text-xs font-bold leading-5 text-white">
                  {cantidadNotificaciones > 99 ? '99+' : cantidadNotificaciones}
                </span>
              )}
            </button>
            {notificacionesAbiertas && (
              <div className="absolute right-0 top-full z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] animate-menu-in overflow-hidden rounded-xl border border-slate-700/80 bg-slate-900/95 shadow-2xl shadow-black/40 backdrop-blur-sm">
                <div className="flex items-center justify-between border-b border-slate-700/80 px-4 py-3">
                  <h2 className="text-sm font-semibold text-slate-100">Facturas pendientes</h2>
                  <span className="text-xs text-slate-400">{cantidadNotificaciones}</span>
                </div>
                {notificaciones.length === 0 ? (
                  <p className="px-4 py-5 text-sm text-slate-400">No hay notificaciones pendientes.</p>
                ) : (
                  <ul className="max-h-80 overflow-y-auto">
                    {notificaciones.map((notificacion) => (
                      <li key={notificacion.id} className="border-b border-slate-800 last:border-b-0">
                        <button
                          type="button"
                          onClick={() => {
                            setNotificacionesAbiertas(false);
                            navigate('/facturacion');
                          }}
                          className="w-full px-4 py-3 text-left text-sm text-slate-200 transition-colors hover:bg-slate-800"
                        >
                          <span className="block">{notificacion.mensaje}</span>
                          <span className="mt-1 block text-xs text-slate-500">
                            {new Date(notificacion.fecha_creacion).toLocaleString('es-ES')}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white shadow-md shadow-brand-orange/20 transition-colors hover:bg-opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/60"
          >
            <LogOut className="h-4 w-4" />
            Salir
          </button>
        </div>
      )}
    </header>
  );
};

export default Encabezado;
